/**
 * Katalog: High-Ticket Beratungsprotokoll & Kostenvoranschlag
 * Dokument-ID: PEPAPERS-SALES-QUOTE-01
 */

export const CONSULT_QUOTE_DOC_ID = 'PEPAPERS-SALES-QUOTE-01';

export const CONSULT_QUOTE_TITLE =
  'BERATUNGSPROTOKOLL & DIENSTLEISTUNGS-KOSTENVORANSCHLAG';

export const CONSULT_QUOTE_SUBTITLE =
  'Fachlicher Kostenvoranschlag und Behandlungsvereinbarung für anspruchsvolle Friseurdienstleistungen';

export const CONSULT_FEASIBILITY_HINT =
  'Farbnuancen können je nach Untergrundpigmentierung und Haarstruktur von Foto-Vorlagen abweichen.';

export const CONSULT_BUFFER_PERCENT = 15 as const;

export const CONSULT_BUFFER_CLAUSE =
  'Kostenvoranschlag & Abrechnung nach tatsächlichem Aufwand: Der ausgewiesene Gesamtbetrag stellt eine fachliche Kalkulation auf Basis des geschätzten Aufwands dar. Die finale Abrechnung erfolgt transparent nach tatsächlichem Zeit- und Materialeinsatz. Sollte sich während der Behandlung herausstellen, dass aufgrund der Haarsituation, extremer Saugfähigkeit oder Altpigmenten ein Mehraufwand entsteht (inkl. Mehrverbrauch je 10g), wird dieser nach tatsächlichem Anfall berechnet. Bei geringerem Zeit- oder Materialaufwand reduziert sich der Betrag entsprechend.';

/** Klausel Material-Mehrverbrauch — {price} wird durch den vereinbarten 10g-Satz ersetzt. */
export function buildConsultMaterialSurchargeClause(priceEur: string): string {
  const display = String(priceEur || '').trim() || '____';
  return (
    'Material-Mehrverbrauch: Sollte aufgrund außergewöhnlicher Haarlänge, Haardichte oder extremer Saugfähigkeit ein unvorhersehbarer chemischer Mehraufwand entstehen, wird der zusätzliche Materialeinsatz mit ' +
    display +
    ' € je 10g abgerechnet.'
  );
}

export const CONSULT_RISK_NOTICE =
  'Der Kunde versichert ausdrücklich die Vollständigkeit und Richtigkeit aller Angaben zur chemischen Vorgeschichte (insbesondere Drogeriefarben, Henna/Pflanzenfarben, Metallsalze, Keratinglättung oder Dauerwellen). Bei unvollständigen, fehlerhaften oder wahrheitswidrigen Angaben entfällt jegliche Haftung oder Gewährleistung für Folgeschäden (wie unerwartete Farbumschläge, Fleckenbildung, Strukturschäden oder Haarbruch). Sämtliche Regress- und Schadensersatzansprüche gegen den Salon und den Stylisten sind in diesem Fall vollumfänglich ausgeschlossen. Bei stark vorbelastetem Haar kann es trotz fachgerechter Ausführung zu Strukturverlust kommen.';

export const CONSULT_HOMECARE_CLAUSE =
  'Für die Haltbarkeit des Farbergebnisses und den Erhalt der Haarstruktur wird die Verwendung salonspezifischer Systempflege dringend empfohlen. Für Schäden oder Farbveränderungen durch ungeeignete Nachbehandlung oder Fremdprodukte zu Hause entfällt die Gewährleistung.';

/** Freiwillige Haltbarkeits- & Farbfrische-Garantie — Pflege-Vereinbarung (Dokumenttext). */
export const CONSULT_PFLEGE_GARANTIE_CLAUSE =
  'Das Friseurhandwerk schuldet die fachgerechte Erbringung der Dienstleistung. Die langfristige Haltbarkeit, Brillanz und Haarstruktur hängen nachweislich von der anschließenden Heimpflege ab. Unsere freiwillige Haltbarkeitsgarantie gilt unter der Voraussetzung, dass die Heimpflege lückenlos mit der nachfolgend vereinbarten Wirkstoff-Routine durchgeführt wird:';

export const CONSULT_PFLEGE_GARANTIE_ACK =
  'Die empfohlene Pflegeroutine wurde ausführlich erläutert. Mir ist bekannt, dass die Haltbarkeitsgarantie an die Einhaltung dieser Vorgaben gebunden ist.';

export const CONSULT_ORDER_CONFIRMATION =
  'Ich bestätige die Beratung, den Kostenrahmen sowie die Vereinbarungen zu Anzahlung, Terminverbindlichkeit und Ausfallentschädigung und erteile verbindlich den Auftrag.';

export interface ConsultGarantieProdukt {
  id: string;
  name: string;
  /** z. B. "1x wöchentlich Silver-Maske", "Täglich Color-Save Shampoo" */
  anwendung: string;
  preisBrutto: number;
}
export type ConsultHairBase =
  | 'naturhaar'
  | 'gefaerbt_getoent'
  | 'blondiert_straehnen'
  | 'hellergefaerbt';

export type ConsultPriorTreatment =
  | 'drogerie_heimfarbe'
  | 'henna_pflanzenfarbe'
  | 'keratin_dauerwelle'
  | 'metallsalze_brunnenwasser';

export type ConsultHairCondition =
  | 'gesund'
  | 'trocken_poroes'
  | 'stark_geschaedigt_bruchrisiko';

export type ConsultStrandTest = 'ja' | 'nein_abgelehnt';

export type ConsultFeasibility = 'eine_sitzung' | 'mehrstufenprozess';

export type ConsultLineItemId =
  | 'beratung_vorbereitung'
  | 'farb_blondiertechnik'
  | 'pflege_bondbuilder'
  | 'schnitt_styling'
  | 'heimpflege_material';

export interface ConsultSalonData {
  firmenname: string;
  strasse: string;
  plz: string;
  ort: string;
}

export interface ConsultCustomerData {
  vorname: string;
  nachname: string;
  telefon: string;
  email: string;
}

export interface ConsultAppointmentData {
  dateIso: string;
  stylistName: string;
}

export interface ConsultRiskAnalysis {
  hairBase: ConsultHairBase[];
  priorTreatments: ConsultPriorTreatment[];
  customPriorTreatments: string;
  hairCondition: ConsultHairCondition | '';
  customConditionNotes: string;
  strandTest: ConsultStrandTest | '';
  strandTestResult: string;
}

export interface ConsultGoalDefinition {
  customerWish: string;
  feasibility: ConsultFeasibility | '';
  plannedSessions: string;
}

export interface ConsultLineItem {
  id: string;
  label: string;
  timeEffort: string;
  amountEur: string;
  /** Nur relevant für Verkauf/Heimpflege: inklusive = nicht dazugerechnet */
  inclusive?: boolean;
}

export interface ConsultProductLine {
  id: string;
  label: string;
  amountEur: string;
  /** true = im Preis enthalten / nur erwähnt; false = extra auf Rechnung */
  inclusive: boolean;
}

export type PeConsultationZahlungsart = 'Bar' | 'EC-Karte' | 'Überweisung' | 'PayPal';

export interface PeConsultationDepositConfig {
  anzahlungAktiv: boolean;
  anzahlungTyp: 'prozent' | 'festbetrag';
  anzahlungWert: number;
  stornoFristStunden: number;
  ausfallQuoteProzent: number;
  zahlungsart?: PeConsultationZahlungsart;
  /** Format: DD.MM.YYYY */
  bezahltAm?: string;
}

export const CONSULT_DEFAULT_DEPOSIT_CONFIG: PeConsultationDepositConfig = {
  anzahlungAktiv: false,
  anzahlungTyp: 'prozent',
  anzahlungWert: 30,
  stornoFristStunden: 48,
  ausfallQuoteProzent: 60,
};

/** Anzahlungsbetrag aus Bruttosumme + Deposit-Config. */
export function calcConsultDepositAmountEur(
  gesamtSummeBrutto: number,
  deposit: PeConsultationDepositConfig
): number {
  if (!deposit.anzahlungAktiv) return 0;
  const wert = Number(deposit.anzahlungWert);
  if (!(wert > 0)) return 0;
  if (deposit.anzahlungTyp === 'festbetrag') return wert;
  if (!(gesamtSummeBrutto > 0)) return 0;
  return (gesamtSummeBrutto * wert) / 100;
}

export interface ConsultPricing {
  lineItems: ConsultLineItem[];
  totalEur: string;
  bufferPercent: typeof CONSULT_BUFFER_PERCENT;
  materialSurchargePer10g: string;
  /** Welches Bond-Produkt angewendet wurde */
  bondProductName: string;
  /** Bond inkl. im STD-Satz (kein Extra-Preis) */
  bondProductInclusive: boolean;
  /** Extra-Preis Bond nur wenn nicht inklusive */
  bondProductAmountEur: string;
  /** Verkaufsprodukte / Mitnahme */
  salesProducts: ConsultProductLine[];
  /** Anzahlung / No-Show / Härtefall */
  deposit: PeConsultationDepositConfig;
  /** Optionale freiwillige Farbfrische- & Haltbarkeitsgarantie */
  hasPflegeGarantie: boolean;
  /** Garantiezeitraum in Wochen (Default 4) */
  garantieWochen: number;
  /** Verbindlich vereinbarte Pflegeprodukte inkl. Anwendung */
  garantieProdukte: ConsultGarantieProdukt[];
  /** true = Summe der Garantie-Produkte in den Gesamtpreis einrechnen */
  garantiePflegesetInGesamtpreis: boolean;
}

export interface ConsultLiabilityAck {
  riskNoticeAccepted: boolean;
  homeCareNoticeAccepted: boolean;
}

export interface ConsultSignatures {
  place: string;
  dateIso: string;
}

export interface ConsultationQuoteFormData {
  salon: ConsultSalonData;
  customer: ConsultCustomerData;
  appointment: ConsultAppointmentData;
  risk: ConsultRiskAnalysis;
  goal: ConsultGoalDefinition;
  pricing: ConsultPricing;
  liability: ConsultLiabilityAck;
  signatures: ConsultSignatures;
}

export const CONSULT_HAIR_BASE_OPTIONS: Array<{ id: ConsultHairBase; label: string }> = [
  { id: 'naturhaar', label: 'Naturhaar' },
  { id: 'gefaerbt_getoent', label: 'Gefärbt / Getönt' },
  { id: 'blondiert_straehnen', label: 'Blondiert / Strähnen' },
  { id: 'hellergefaerbt', label: 'Hellergefärbt' },
];

export const CONSULT_PRIOR_OPTIONS: Array<{ id: ConsultPriorTreatment; label: string }> = [
  { id: 'drogerie_heimfarbe', label: 'Drogerie-/Heimfarben' },
  { id: 'henna_pflanzenfarbe', label: 'Henna / Pflanzenfarbe' },
  { id: 'keratin_dauerwelle', label: 'Keratinglättung / Dauerwelle' },
  { id: 'metallsalze_brunnenwasser', label: 'Metallsalze / Brunnenwasser' },
];

export const CONSULT_CONDITION_OPTIONS: Array<{ id: ConsultHairCondition; label: string }> = [
  { id: 'gesund', label: 'Gesund' },
  { id: 'trocken_poroes', label: 'Trocken / Porös' },
  { id: 'stark_geschaedigt_bruchrisiko', label: 'Stark geschädigt / Haarbruchrisiko' },
];

export const CONSULT_DEFAULT_LINE_ITEMS: ConsultLineItem[] = [
  {
    id: 'beratung_vorbereitung',
    label: 'Beratung, Analyse & Vorbereitung',
    timeEffort: '',
    amountEur: '',
  },
  {
    id: 'farb_blondiertechnik',
    label: 'Farb- / Blondiertechnik / Veredelung (Glossing)',
    timeEffort: '',
    amountEur: '',
  },
  {
    id: 'pflege_bondbuilder',
    label: 'Pflegebehandlung / Bond-Builder',
    timeEffort: '',
    amountEur: '',
  },
  {
    id: 'schnitt_styling',
    label: 'Schnitt, Styling & Finish',
    timeEffort: '',
    amountEur: '',
  },
  {
    id: 'heimpflege_material',
    label: 'Heimpflege-Empfehlung / Materialpaket',
    timeEffort: '',
    amountEur: '',
    inclusive: false,
  },
];

const todayIso = () => new Date().toISOString().split('T')[0];

export function getDefaultConsultationQuoteData(
  salon?: Partial<ConsultSalonData>
): ConsultationQuoteFormData {
  return {
    salon: {
      firmenname: salon?.firmenname || '',
      strasse: salon?.strasse || '',
      plz: salon?.plz || '',
      ort: salon?.ort || '',
    },
    customer: { vorname: '', nachname: '', telefon: '', email: '' },
    appointment: {
      dateIso: todayIso(),
      stylistName: '',
    },
    risk: {
      hairBase: [],
      priorTreatments: [],
      customPriorTreatments: '',
      hairCondition: '',
      customConditionNotes: '',
      strandTest: '',
      strandTestResult: '',
    },
    goal: {
      customerWish: '',
      feasibility: '',
      plannedSessions: '',
    },
    pricing: {
      lineItems: CONSULT_DEFAULT_LINE_ITEMS.map((i) => ({ ...i })),
      totalEur: '',
      bufferPercent: CONSULT_BUFFER_PERCENT,
      materialSurchargePer10g: '',
      bondProductName: '',
      bondProductInclusive: true,
      bondProductAmountEur: '',
      salesProducts: [],
      deposit: { ...CONSULT_DEFAULT_DEPOSIT_CONFIG },
      hasPflegeGarantie: false,
      garantieWochen: 4,
      garantieProdukte: [],
      garantiePflegesetInGesamtpreis: false,
    },
    liability: {
      riskNoticeAccepted: false,
      homeCareNoticeAccepted: false,
    },
    signatures: {
      place: salon?.ort || '',
      dateIso: todayIso(),
    },
  };
}
